import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, TrendingUp, Trash2, PiggyBank, AlertTriangle, Upload, FileDown, FileText } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { useProperty } from "@/contexts/PropertyContext";
import { API } from "@/lib/api";

const CATEGORIES = ["roof", "hvac", "paving", "paint", "elevator", "pool", "landscape", "electrical", "plumbing", "structural", "other"];

function AddComponentDialog({ propertyId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "other", useful_life_years: 20, remaining_life_years: 15, current_cost_usd: 100000, allocated_reserves_usd: 0 });
  const save = async () => {
    if (!propertyId) return toast.error("Pick a property");
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/admin/reserve/components", { property_id: propertyId, ...form }); toast.success("Component added"); setOpen(false); onSaved(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-component-btn" className="bg-orange-500 hover:bg-orange-600 rounded-full"><Plus className="h-4 w-4 mr-1" /> Component</Button></DialogTrigger>
      <DialogContent className="bg-card border-white/10">
        <DialogHeader><DialogTitle>Add reserve component</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="comp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main clubhouse roof" className="mt-1 bg-white/[0.04] border-white/10" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="comp-category" className="mt-1 bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Useful life (yrs)</Label><Input data-testid="comp-life" type="number" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: parseInt(e.target.value, 10) || 1 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Remaining (yrs)</Label><Input data-testid="comp-remaining" type="number" value={form.remaining_life_years} onChange={(e) => setForm({ ...form, remaining_life_years: parseInt(e.target.value, 10) || 0 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Current cost $</Label><Input data-testid="comp-cost" type="number" value={form.current_cost_usd} onChange={(e) => setForm({ ...form, current_cost_usd: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div className="col-span-2"><Label>Currently reserved $</Label><Input type="number" value={form.allocated_reserves_usd} onChange={(e) => setForm({ ...form, allocated_reserves_usd: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          </div>
          <Button data-testid="save-component" onClick={save} className="w-full bg-orange-500 hover:bg-orange-600">Add component</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddScenarioDialog({ propertyId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "Base plan", monthly_contribution_usd: 5000, starting_balance_usd: 100000, interest_rate_pct: 2.5 });
  const save = async () => {
    if (!propertyId) return toast.error("Pick a property");
    try { await api.post("/admin/reserve/scenarios", { property_id: propertyId, ...form }); toast.success("Scenario saved"); setOpen(false); onSaved(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-scenario-btn" size="sm" variant="outline" className="border-white/10"><Plus className="h-3 w-3 mr-1" /> Scenario</Button></DialogTrigger>
      <DialogContent className="bg-card border-white/10">
        <DialogHeader><DialogTitle>New funding scenario</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="scen-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Monthly contribution $</Label><Input data-testid="scen-monthly" type="number" value={form.monthly_contribution_usd} onChange={(e) => setForm({ ...form, monthly_contribution_usd: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Starting balance $</Label><Input type="number" value={form.starting_balance_usd} onChange={(e) => setForm({ ...form, starting_balance_usd: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div className="col-span-2"><Label>Interest rate %</Label><Input type="number" step="0.1" value={form.interest_rate_pct} onChange={(e) => setForm({ ...form, interest_rate_pct: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          </div>
          <Button data-testid="save-scenario" onClick={save} className="w-full bg-orange-500 hover:bg-orange-600">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UploadedStudiesPanel({ propertyId }) {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [engineer, setEngineer] = useState("");
  const [studyYear, setStudyYear] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = propertyId ? `?property_id=${propertyId}` : "";
      const { data } = await api.get(`/admin/reserve/documents${q}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return toast.error("Pick a PDF first");
    if (!propertyId) return toast.error("Pick a property");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("property_id", propertyId);
    if (label) fd.append("label", label);
    if (engineer) fd.append("engineer", engineer);
    if (studyYear) fd.append("study_year", studyYear);
    setBusy(true);
    try {
      await api.post("/admin/reserve/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setFile(null); setLabel(""); setEngineer(""); setStudyYear("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Archive this study?")) return;
    try { await api.delete(`/admin/reserve/documents/${id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <Card className="border-white/10 bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-2">
          <FileText className="h-4 w-4 text-orange-400" /> Third-party reserve studies
        </div>
        <Badge variant="outline" className="text-[10px] border-white/10">
          {items.length} uploaded
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 rounded-md bg-white/[0.02] border border-white/5 mb-4">
        <div className="md:col-span-2">
          <Label className="text-xs">PDF file</Label>
          <Input type="file" accept="application/pdf"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="bg-white/[0.04] border-white/10 mt-1 text-xs h-9"
                 data-testid="study-file-input" />
        </div>
        <div>
          <Label className="text-xs">Label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)}
                 placeholder="2026 Reserve Study"
                 className="bg-white/[0.04] border-white/10 mt-1 h-9"
                 data-testid="study-label" />
        </div>
        <div>
          <Label className="text-xs">Engineer</Label>
          <Input value={engineer} onChange={(e) => setEngineer(e.target.value)}
                 placeholder="Association Reserves"
                 className="bg-white/[0.04] border-white/10 mt-1 h-9"
                 data-testid="study-engineer" />
        </div>
        <div>
          <Label className="text-xs">Year</Label>
          <Input type="number" min={1990} max={2100}
                 value={studyYear}
                 onChange={(e) => setStudyYear(e.target.value)}
                 placeholder="2026"
                 className="bg-white/[0.04] border-white/10 mt-1 h-9"
                 data-testid="study-year" />
        </div>
        <div className="md:col-span-5">
          <Button onClick={upload} disabled={busy || !file}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full h-9 w-full"
                  data-testid="study-upload-btn">
            <Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Upload study"}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
          No reserve studies uploaded yet. Drop your engineer's PDF here so residents + boards can pull it.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="rounded border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between"
                 data-testid={`study-row-${d.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">
                  {d.label}
                  {d.study_year && (
                    <Badge variant="outline" className="border-white/10 text-[10px] ml-1">
                      {d.study_year}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {d.engineer ? `${d.engineer} · ` : ""}
                  {Math.round((d.size || 0) / 1024)} KB · uploaded{" "}
                  {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`${API}/admin/reserve/documents/${d.id}/download`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                   data-testid={`study-download-${d.id}`}>
                  <FileDown className="h-3 w-3" /> Download
                </a>
                <button onClick={() => del(d.id)}
                        className="text-zinc-500 hover:text-rose-400"
                        data-testid={`study-del-${d.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function ReserveStudyPage() {
  const { active } = useProperty();
  const propertyId = active?.id;
  const [components, setComponents] = useState([]);
  const [totals, setTotals] = useState({});
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [projection, setProjection] = useState(null);
  const [years, setYears] = useState(30);

  const loadComps = useCallback(async () => {
    try { const q = propertyId ? `?property_id=${propertyId}` : ""; const { data } = await api.get(`/admin/reserve/components${q}`); setComponents(data.items || []); setTotals(data.totals || {}); }
    catch { setComponents([]); }
  }, [propertyId]);
  const loadScenarios = useCallback(async () => {
    try { const q = propertyId ? `?property_id=${propertyId}` : ""; const { data } = await api.get(`/admin/reserve/scenarios${q}`); setScenarios(data.items || []); if ((data.items || []).length && !activeScenarioId) setActiveScenarioId(data.items[0].id); }
    catch { setScenarios([]); }
  }, [propertyId, activeScenarioId]);
  const loadProjection = useCallback(async () => {
    if (!activeScenarioId) return setProjection(null);
    try { const { data } = await api.get(`/admin/reserve/projection?scenario_id=${activeScenarioId}&years=${years}`); setProjection(data); }
    catch (e) { toast.error(apiErrorMessage(e)); setProjection(null); }
  }, [activeScenarioId, years]);

  useEffect(() => { loadComps(); loadScenarios(); }, [loadComps, loadScenarios]);
  useEffect(() => { loadProjection(); }, [loadProjection]);

  const chartData = useMemo(() => (projection?.projection || []).map((p) => ({ year: `Y${p.year}`, balance: p.end_balance })), [projection]);

  const delComp = async (id) => {
    if (!window.confirm("Delete this component?")) return;
    try { await api.delete(`/admin/reserve/components/${id}`); loadComps(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Financials" title="Reserve Study" subtitle="Capital planning — components, funding scenarios, and 30-year projections." />
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Components</div><div className="text-2xl font-semibold" data-testid="comp-count">{components.length}</div></Card>
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Current cost total</div><div className="text-2xl font-semibold">${(totals.current_cost_usd || 0).toLocaleString()}</div></Card>
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Percent funded</div><div className="text-2xl font-semibold text-emerald-300" data-testid="pct-funded">{totals.percent_funded || 0}%</div></Card>
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Unfunded</div><div className="text-2xl font-semibold text-rose-300">${(totals.unfunded_usd || 0).toLocaleString()}</div></Card>
      </div>

      <Tabs defaultValue="components">
        <TabsList className="bg-white/[0.03] border border-white/10">
          <TabsTrigger value="components" data-testid="tab-components">Components</TabsTrigger>
          <TabsTrigger value="projection" data-testid="tab-projection">30-yr projection</TabsTrigger>
          <TabsTrigger value="studies" data-testid="tab-studies">Uploaded studies</TabsTrigger>
        </TabsList>

        <TabsContent value="components" className="mt-4">
          <Card className="border-white/10 bg-card p-4">
            <div className="flex items-center justify-between mb-3"><div className="overline">Physical assets</div><AddComponentDialog propertyId={propertyId} onSaved={loadComps} /></div>
            <div className="space-y-2">
              {components.map((c) => (
                <div key={c.id} className="rounded border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between" data-testid={`component-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.name} <Badge variant="outline" className="border-white/10 text-[10px] ml-1">{c.category}</Badge></div>
                    <div className="text-xs text-zinc-500 mt-1">Life {c.useful_life_years}yr · Remaining {c.remaining_life_years}yr · Cost ${c.current_cost_usd?.toLocaleString()} · Reserved ${c.allocated_reserves_usd?.toLocaleString()} · Funding needed ${c.annual_funding_needed_usd?.toLocaleString()}/yr</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={c.percent_funded >= 80 ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : c.percent_funded >= 40 ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-rose-500/40 text-rose-300 bg-rose-500/10"}>{c.percent_funded}% funded</Badge>
                    <button onClick={() => delComp(c.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {components.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Add your first component to start the reserve study.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="projection" className="mt-4">
          <Card className="border-white/10 bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="overline">Funding scenario</div>
                <Select value={activeScenarioId || ""} onValueChange={setActiveScenarioId}>
                  <SelectTrigger data-testid="scenario-select" className="w-56 bg-white/[0.04] border-white/10"><SelectValue placeholder="Select scenario…" /></SelectTrigger>
                  <SelectContent>{scenarios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} (${s.monthly_contribution_usd}/mo)</SelectItem>)}</SelectContent>
                </Select>
                <AddScenarioDialog propertyId={propertyId} onSaved={loadScenarios} />
                <Input type="number" value={years} onChange={(e) => setYears(parseInt(e.target.value, 10) || 30)} className="w-20 bg-white/[0.04] border-white/10 text-xs" data-testid="years-input" />
                <span className="text-xs text-zinc-500">years</span>
              </div>
              {projection?.ends_negative && <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10" data-testid="warn-negative"><AlertTriangle className="h-3 w-3 mr-1" /> Goes negative at Y{projection.min_balance_year}</Badge>}
              {activeScenarioId && (
                <a href={`${API}/admin/reserve/projection.pdf?scenario_id=${activeScenarioId}&years=${years}`} target="_blank" rel="noreferrer"
                   data-testid="dl-reserve-pdf"
                   className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-orange-500/40 text-orange-300 bg-orange-500/10 hover:bg-orange-500/20">
                  <FileDown className="h-3 w-3" /> Board-ready PDF
                </a>
              )}
            </div>
            {projection ? (
              <>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <Card className="border-white/10 bg-white/[0.02] p-3"><div className="overline text-[10px]">Final balance</div><div className="text-xl font-semibold">${projection.final_balance_usd.toLocaleString()}</div></Card>
                  <Card className="border-white/10 bg-white/[0.02] p-3"><div className="overline text-[10px]">Min balance</div><div className={`text-xl font-semibold ${projection.min_balance_usd < 0 ? "text-rose-300" : "text-emerald-300"}`}>${projection.min_balance_usd.toLocaleString()}</div></Card>
                  <Card className="border-white/10 bg-white/[0.02] p-3"><div className="overline text-[10px]">Min-balance year</div><div className="text-xl font-semibold">Y{projection.min_balance_year || "—"}</div></Card>
                </div>
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="year" stroke="#71717a" fontSize={11} />
                      <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <RTooltip contentStyle={{ background: "#0a0a0b", border: "1px solid #27272a" }} formatter={(v) => `$${v.toLocaleString()}`} />
                      <ReferenceLine y={0} stroke="#f43f5e" strokeDasharray="4 4" />
                      <Line type="monotone" dataKey="balance" stroke="#f97316" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground py-8 text-center"><PiggyBank className="h-8 w-8 mx-auto mb-2 opacity-50" /> Create a funding scenario to see the 30-year projection.</div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="studies" className="mt-4">
          <UploadedStudiesPanel propertyId={propertyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
